import { useState } from 'react';
import { Trash2, Scissors } from 'lucide-react';
import type { SliceRect } from '../types';

interface SlicePanelProps {
  slices: readonly SliceRect[];
  onUpdate: (id: string, patch: Partial<Omit<SliceRect, 'id'>>) => void;
  onRemove: (id: string) => void;
  onClearAll: () => void;
  /** Canvas dimensions — used purely for the inline hint when the
   *  list is empty ("Drag on the 1920 × 1080 canvas…"); the
   *  reducer does NOT clamp slices to canvas bounds at edit time
   *  (a user dragging a slice partially off-canvas is intentional
   *  and the export pipeline clamps at rasterization time). */
  canvasWidth: number;
  canvasHeight: number;
}

/** Tier 2 Group F sub-piece #5 — SlicePanel.
 *
 *  The properties-panel surface for the Slice tool. Shows the
 *  defined slice list (one row per slice — name + W × H + delete
 *  button), plus a "Clear all" action and a hint when the list
 *  is empty.
 *
 *  Slice geometry is managed primarily on the canvas (drag-to-
 *  create new slice; Fabric's native controls handle resize +
 *  move). This panel intentionally keeps the geometry editing
 *  minimal — name + delete — so it doesn't compete with the
 *  spatial canvas controls. A future enhancement could add
 *  numeric X/Y/W/H inputs per slice when keyboard-only editing
 *  becomes a real workflow. */
export function SlicePanel({
  slices,
  onUpdate,
  onRemove,
  onClearAll,
  canvasWidth,
  canvasHeight,
}: SlicePanelProps) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold flex items-center gap-1.5">
          <Scissors className="h-4 w-4" aria-hidden="true" />
          Slices
          <span className="text-xs font-normal text-muted-foreground">({slices.length})</span>
        </h3>
        {slices.length > 0 && (
          <button
            type="button"
            onClick={() => {
              if (slices.length === 1 || window.confirm(`Delete all ${slices.length} slices?`)) {
                onClearAll();
              }
            }}
            className="text-xs text-muted-foreground hover:text-destructive transition-colors"
          >
            Clear all
          </button>
        )}
      </div>

      {slices.length === 0 ? (
        <div className="rounded-md border border-dashed border-border bg-muted/30 p-4 text-center">
          <p className="text-sm text-muted-foreground">Drag on the canvas to create a slice.</p>
          {canvasWidth > 0 && canvasHeight > 0 ? (
            <p className="mt-1 text-xs text-muted-foreground/70">
              Canvas: {canvasWidth} × {canvasHeight}px
            </p>
          ) : null}
          <p className="mt-2 text-xs text-muted-foreground/70">
            Each slice exports as a separate file from the Save dialog when Region → Slices is
            selected.
          </p>
        </div>
      ) : (
        <ul className="space-y-1.5">
          {slices.map((slice, idx) => (
            <SliceRow
              key={slice.id}
              slice={slice}
              index={idx + 1}
              onRename={(name) => onUpdate(slice.id, { name })}
              onRemove={() => onRemove(slice.id)}
            />
          ))}
        </ul>
      )}

      <p className="text-xs text-muted-foreground/70 leading-relaxed">
        Hint: drag a slice on the canvas to move it; drag a corner to resize. Hold Shift while
        dragging on an empty area to constrain a new slice to a square.
      </p>
    </div>
  );
}

interface SliceRowProps {
  slice: SliceRect;
  index: number;
  onRename: (name: string) => void;
  onRemove: () => void;
}

function SliceRow({ slice, index, onRename, onRemove }: SliceRowProps) {
  // Local draft state for inline rename. Commits on blur or Enter.
  // The reducer treats an empty rename as "keep current name" via
  // the trim + fallback in ADD_SLICE; UPDATE_SLICE always trusts the
  // caller, so we guard empty here at the row level.
  const [draft, setDraft] = useState(slice.name);
  const [editing, setEditing] = useState(false);

  const commitRename = () => {
    const trimmed = draft.trim();
    if (!trimmed) {
      // Revert to current name on empty submit.
      setDraft(slice.name);
      setEditing(false);
      return;
    }
    if (trimmed !== slice.name) {
      onRename(trimmed);
    }
    setEditing(false);
  };

  return (
    <li className="flex items-center gap-2 px-2 py-1.5 rounded-md border border-border bg-background hover:bg-accent/30 transition-colors">
      <span className="text-xs font-mono text-muted-foreground/60 w-5 text-right">{index}</span>
      {editing ? (
        <input
          type="text"
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commitRename}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commitRename();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              setDraft(slice.name);
              setEditing(false);
            }
          }}
          className="flex-1 px-1.5 py-0.5 text-sm bg-background border border-border rounded"
          aria-label={`Rename slice ${slice.name}`}
        />
      ) : (
        <button
          type="button"
          onClick={() => {
            setDraft(slice.name);
            setEditing(true);
          }}
          className="flex-1 text-left text-sm hover:underline truncate"
          title={`Rename ${slice.name}`}
        >
          {slice.name}
        </button>
      )}
      <span className="text-xs text-muted-foreground tabular-nums whitespace-nowrap">
        {slice.width} × {slice.height}
      </span>
      <button
        type="button"
        onClick={onRemove}
        className="p-1 text-muted-foreground hover:text-destructive transition-colors"
        title={`Delete ${slice.name}`}
        aria-label={`Delete ${slice.name}`}
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </li>
  );
}
